import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL =
  "https://dcdws.blob.core.windows.net/dws-19661937-81179-media/sites/81179/2024/12/united-auto1-w300.png";

const internalLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About Us", to: "/about" },
  { label: "Location", to: "/location" },
];

const externalLinks = [
  { label: "Inventory", href: "https://www.uagmn.com/inventory/" },
  { label: "Apply Online", href: "https://www.uagmn.com/apply-online/" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Gold accent bar — always visible */}
      <div className="fixed top-0 left-0 right-0 z-[51] h-0.5 bg-gradient-to-r from-transparent via-gold-DEFAULT to-transparent" />

      <nav
        className={`fixed top-0.5 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled
            ? "bg-white/97 backdrop-blur-md shadow-sm py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 cursor-pointer" aria-label="United Auto Group Home">
            <img
              src={LOGO_URL}
              alt="United Auto Group"
              className={`h-9 w-auto object-contain transition-all duration-300 ${
                scrolled ? "opacity-100" : "brightness-0 invert"
              }`}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {internalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-xs font-semibold tracking-[0.12em] uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled ? "text-charcoal" : "text-white/90"
                } ${
                  location.pathname === link.to
                    ? "text-gold-DEFAULT"
                    : "hover:text-gold-DEFAULT"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {externalLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                rel="noopener noreferrer"
                className={`text-xs font-semibold tracking-[0.12em] uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled ? "text-charcoal" : "text-white/90"
                } hover:text-gold-DEFAULT`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href="tel:9527693208"
              className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? "text-charcoal" : "text-white/85"
              } hover:text-gold-DEFAULT`}
              aria-label="Call United Auto Group (952) 769-3208"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-gold-DEFAULT text-base" />
              </div>
              (952) 769-3208
            </a>
            <a
              href="https://www.uagmn.com/get-pre-qualified/"
              rel="noopener noreferrer"
              className="bg-gold-DEFAULT text-charcoal text-xs font-bold px-6 py-2.5 rounded-full tracking-[0.1em] uppercase hover:bg-gold-light transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Get Pre-Qualified
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`lg:hidden p-2 transition-colors cursor-pointer ${
              scrolled ? "text-charcoal" : "text-white"
            }`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle navigation menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            mobileOpen ? "max-h-screen" : "max-h-0"
          } bg-white border-t border-cream-dark`}
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {internalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-semibold tracking-wide uppercase text-charcoal hover:text-gold-DEFAULT transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname === link.to ? "text-gold-DEFAULT" : ""
                }`}
              >
                {link.label}
              </Link>
            ))}
            {externalLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                rel="noopener noreferrer"
                className="text-sm font-semibold tracking-wide uppercase text-charcoal hover:text-gold-DEFAULT transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-cream-dark pt-5 flex flex-col gap-3">
              <a
                href="tel:9527693208"
                className="flex items-center gap-2 text-charcoal font-semibold cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-gold-DEFAULT" />
                </div>
                (952) 769-3208
              </a>
              <a
                href="https://www.uagmn.com/get-pre-qualified/"
                rel="noopener noreferrer"
                className="bg-gold-DEFAULT text-charcoal text-xs font-bold px-5 py-3.5 rounded-full text-center cursor-pointer whitespace-nowrap tracking-wide uppercase"
              >
                Get Pre-Qualified
              </a>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
